import { FederatedAuthServiceImpl } from "~/backend/generated/FederatedAuth/api/federatedAuth.service";
import { FederatedAuthService } from "~/backend/generated/FederatedAuth/api/federatedAuth.serviceInterface";
import { SsoSetupService } from "~/backend/generated/SSOSetup/api/ssoSetup.serviceInterface";
import { SsoSetupServiceImpl } from "~/backend/generated/SSOSetup/api/ssoSetup.service";
import { AuthenticationConfigResponse } from "~/backend/generated/SSOSetup/model/authenticationConfigResponse";
import { Authenticator, IdpConfig } from "~/modules/auth";
import { AuthenticationConfigParams, TokenData, TokenRefreshResult } from "~/modules/auth/Authenticator/Authenticator";
import { AuthenticatorDataContainer } from "~/modules/auth/AuthenticatorDataContainer/AuthenticatorDataContainer";
import { ContextManager } from "~/modules/contextManager/ContextManager";
import { InternalError } from "~/modules/error";
import { getLogger, Logger, LoggerName } from "~/modules/logger";
import { assertDefined, assertNotEmptyString, assertObject } from "~/utils/assert";
import { AuthenticatorDataContainerImpl } from "../AuthenticatorDataContainer/AuthenticatorDataContainerImpl";
import * as AuthenticatorHelper from "./AuthenticatorHelper";

export class AuthenticatorImpl implements Authenticator {
    readonly #authenticatorDataContainer: AuthenticatorDataContainer;

    readonly #logger: Logger;

    readonly #federatedAuthService: FederatedAuthService;

    readonly #ssoSetupService: SsoSetupService;

    constructor(ctx: ContextManager) {
        this.#logger = getLogger(LoggerName.Auth);
        this.#authenticatorDataContainer =
            ctx.getInstanceOf<AuthenticatorDataContainerImpl>(AuthenticatorDataContainerImpl);
        this.#federatedAuthService = ctx.getInstanceOf(FederatedAuthServiceImpl);
        this.#ssoSetupService = ctx.getInstanceOf(SsoSetupServiceImpl);
    }

    async getIdpUrl(config: IdpConfig): Promise<string> {
        assertNotEmptyString(config.redirectUrl, "redirect url");

        const payload = AuthenticatorHelper.getSSOLoginRequestBody(config);
        const accountSid = this.#authenticatorDataContainer.accountSid;

        const data = await this.#federatedAuthService.getIdpUrl(accountSid, payload);
        if (!data.location) {
            this.#logger.error("No redirect location from /authenticate request, data: ", data);
            throw new InternalError("Invalid response from /authenticate endpoint");
        }
        return data.location;
    }

    async validateToken(token: string): Promise<TokenData> {
        const accountSid = this.#authenticatorDataContainer.accountSid;
        const tokenData = await this.#federatedAuthService.validateToken(accountSid, { token }, { token });

        return {
            roles: tokenData.roles,
            valid: tokenData.valid,
            dateExpired: tokenData.expiration,
            identity: tokenData.identity,
            flexUserSid: tokenData.flexUserSid
        };
    }

    async refreshToken(token: string): Promise<TokenRefreshResult> {
        const accountSid = this.#authenticatorDataContainer.accountSid;
        const tokenRefreshResult = await this.#federatedAuthService.refreshToken(accountSid, {
            token
        });
        return { token: tokenRefreshResult.token, dateExpired: tokenRefreshResult.expiration };
    }

    async getAuthenticationConfig(config: AuthenticationConfigParams): Promise<AuthenticationConfigResponse> {
        assertDefined<AuthenticationConfigParams>(config, "config");
        assertObject<AuthenticationConfigParams>(config, "config");

        const accountSid = this.#authenticatorDataContainer.accountSid || "";

        const { instanceSid = "", runtimeDomain = "", sSOProfileSid = "", appType = "SinglePageApp" } = config;

        return this.#ssoSetupService.getAuthenticationConfig(
            accountSid,
            instanceSid,
            runtimeDomain,
            sSOProfileSid,
            appType
        );
    }
}
